import type { 
  Rule, 
  RuleContext, 
  RuleResult,
  LayoutAnalysisResult,
  LayoutIssue
} from "../types/index.js";
import { logger } from "../utils/logger.js";

/**
 * 布局分析器
 */
export class LayoutAnalyzer {
  /**
   * 分析节点的布局结构
   */
  analyzeLayout(node: any): LayoutAnalysisResult {
    const issues: LayoutIssue[] = [];
    const recommendations: string[] = [];

    // 检测布局类型
    const layoutType = this.detectLayoutType(node);
    
    // 评估复杂度
    const complexity = this.evaluateComplexity(node);

    // 检查重叠问题
    const overlapIssues = this.checkOverlaps(node);
    issues.push(...overlapIssues);

    // 检查对齐问题
    const alignmentIssues = this.checkAlignment(node);
    issues.push(...alignmentIssues);

    // 检查间距一致性
    const spacingIssues = this.checkSpacingConsistency(node);
    issues.push(...spacingIssues);

    // 检查层级结构
    const hierarchyIssues = this.checkHierarchy(node);
    issues.push(...hierarchyIssues);

    // 生成建议
    if (layoutType === "mixed") {
      recommendations.push("考虑统一使用自动布局以提高一致性");
    }

    if (complexity === "complex") {
      recommendations.push("简化布局结构，减少嵌套层级");
    }

    if (issues.some(issue => issue.type === "overlap")) {
      recommendations.push("修复元素重叠问题，确保布局清晰");
    }

    return {
      layoutType,
      complexity,
      issues,
      recommendations
    };
  }

  /**
   * 检测布局类型
   */
  private detectLayoutType(node: any): "flex" | "grid" | "absolute" | "mixed" {
    const layoutModes = this.collectLayoutModes(node);
    
    if (layoutModes.has("HORIZONTAL") || layoutModes.has("VERTICAL")) {
      if (layoutModes.has("NONE")) {
        return "mixed";
      }
      return "flex";
    }
    
    if (layoutModes.has("NONE")) {
      return "absolute";
    }

    return "absolute";
  }

  /**
   * 收集所有布局模式
   */
  private collectLayoutModes(node: any): Set<string> {
    const modes = new Set<string>();
    
    if (node.layoutMode) {
      modes.add(node.layoutMode);
    } else {
      modes.add("NONE");
    }

    if (node.children && Array.isArray(node.children)) {
      for (const child of node.children) {
        const childModes = this.collectLayoutModes(child);
        childModes.forEach(mode => modes.add(mode));
      }
    }

    return modes;
  }

  /**
   * 评估布局复杂度
   */
  private evaluateComplexity(node: any): "simple" | "moderate" | "complex" {
    const depth = this.calculateDepth(node);
    const nodeCount = this.countNodes(node);
    
    if (depth <= 3 && nodeCount <= 10) {
      return "simple";
    } else if (depth <= 6 && nodeCount <= 50) {
      return "moderate";
    } else {
      return "complex";
    }
  }

  /**
   * 计算嵌套深度
   */
  private calculateDepth(node: any, currentDepth = 0): number {
    if (!node.children || !Array.isArray(node.children) || node.children.length === 0) {
      return currentDepth;
    }

    let maxDepth = currentDepth;
    for (const child of node.children) {
      const childDepth = this.calculateDepth(child, currentDepth + 1);
      maxDepth = Math.max(maxDepth, childDepth);
    }

    return maxDepth;
  }

  /**
   * 统计节点数量
   */
  private countNodes(node: any): number {
    let count = 1;
    
    if (node.children && Array.isArray(node.children)) {
      for (const child of node.children) {
        count += this.countNodes(child);
      }
    }

    return count;
  }

  /**
   * 检查元素重叠
   */
  private checkOverlaps(node: any): LayoutIssue[] {
    const issues: LayoutIssue[] = [];
    const siblings = this.getSiblings(node);

    for (let i = 0; i < siblings.length; i++) {
      for (let j = i + 1; j < siblings.length; j++) {
        const nodeA = siblings[i];
        const nodeB = siblings[j];

        if (this.isOverlapping(nodeA, nodeB)) {
          issues.push({
            type: "overlap",
            severity: "medium",
            description: `元素 "${nodeA.name}" 与 "${nodeB.name}" 发生重叠`,
            affectedNodes: [nodeA.id, nodeB.id]
          });
        }
      }
    }

    return issues;
  }

  /**
   * 获取兄弟节点
   */
  private getSiblings(node: any): any[] {
    if (!node.children || !Array.isArray(node.children)) {
      return [];
    }
    return node.children.filter((child: any) => child.visible !== false);
  }

  /**
   * 检查两个元素是否重叠
   */
  private isOverlapping(nodeA: any, nodeB: any): boolean {
    const boundsA = nodeA.absoluteBoundingBox;
    const boundsB = nodeB.absoluteBoundingBox;

    if (!boundsA || !boundsB) return false;

    return !(
      boundsA.x + boundsA.width <= boundsB.x ||
      boundsB.x + boundsB.width <= boundsA.x ||
      boundsA.y + boundsA.height <= boundsB.y ||
      boundsB.y + boundsB.height <= boundsA.y
    );
  }

  /**
   * 检查对齐问题
   */
  private checkAlignment(node: any): LayoutIssue[] {
    const issues: LayoutIssue[] = [];
    const siblings = this.getSiblings(node);

    if (siblings.length < 2) return issues;

    // 检查水平对齐
    const horizontalGroups = this.groupByAlignment(siblings, 'horizontal');
    for (const group of horizontalGroups) {
      if (group.length >= 2 && !this.isProperlyAligned(group, 'horizontal')) {
        issues.push({
          type: "misalignment",
          severity: "low",
          description: `${group.length} 个元素水平对齐不准确`,
          affectedNodes: group.map(n => n.id)
        });
      }
    }

    // 检查垂直对齐
    const verticalGroups = this.groupByAlignment(siblings, 'vertical');
    for (const group of verticalGroups) {
      if (group.length >= 2 && !this.isProperlyAligned(group, 'vertical')) {
        issues.push({
          type: "misalignment",
          severity: "low",
          description: `${group.length} 个元素垂直对齐不准确`,
          affectedNodes: group.map(n => n.id)
        });
      }
    }

    return issues;
  }

  /**
   * 按对齐方式分组
   */
  private groupByAlignment(nodes: any[], direction: 'horizontal' | 'vertical'): any[][] {
    const groups: any[][] = [];
    const tolerance = 2; // 2px 容差

    for (const node of nodes) {
      const bounds = node.absoluteBoundingBox;
      if (!bounds) continue;

      const position = direction === 'horizontal' ? bounds.y : bounds.x;
      
      let foundGroup = false;
      for (const group of groups) {
        const groupBounds = group[0].absoluteBoundingBox;
        const groupPosition = direction === 'horizontal' ? groupBounds.y : groupBounds.x;
        
        if (Math.abs(position - groupPosition) <= tolerance) {
          group.push(node);
          foundGroup = true;
          break;
        }
      }

      if (!foundGroup) {
        groups.push([node]);
      }
    }

    return groups.filter(group => group.length >= 2);
  }

  /**
   * 检查是否正确对齐
   */
  private isProperlyAligned(nodes: any[], direction: 'horizontal' | 'vertical'): boolean {
    if (nodes.length < 2) return true;

    const tolerance = 1; // 1px 容差
    const firstBounds = nodes[0].absoluteBoundingBox;
    if (!firstBounds) return true;

    const referencePosition = direction === 'horizontal' ? firstBounds.y : firstBounds.x;

    return nodes.every(node => {
      const bounds = node.absoluteBoundingBox;
      if (!bounds) return true;
      
      const position = direction === 'horizontal' ? bounds.y : bounds.x;
      return Math.abs(position - referencePosition) <= tolerance;
    });
  }

  /**
   * 检查间距一致性
   */
  private checkSpacingConsistency(node: any): LayoutIssue[] {
    const issues: LayoutIssue[] = [];
    const siblings = this.getSiblings(node);

    if (siblings.length < 3) return issues;

    // 检查水平间距
    const horizontalSpacings = this.calculateSpacings(siblings, 'horizontal');
    if (!this.isConsistentSpacing(horizontalSpacings)) {
      issues.push({
        type: "inconsistent_spacing",
        severity: "low",
        description: "元素间水平间距不一致",
        affectedNodes: siblings.map(n => n.id)
      });
    }

    // 检查垂直间距
    const verticalSpacings = this.calculateSpacings(siblings, 'vertical');
    if (!this.isConsistentSpacing(verticalSpacings)) {
      issues.push({
        type: "inconsistent_spacing",
        severity: "low",
        description: "元素间垂直间距不一致",
        affectedNodes: siblings.map(n => n.id)
      });
    }

    return issues;
  }

  /**
   * 计算间距
   */
  private calculateSpacings(nodes: any[], direction: 'horizontal' | 'vertical'): number[] {
    const spacings: number[] = [];
    
    // 按位置排序
    const sortedNodes = [...nodes].sort((a, b) => {
      const boundsA = a.absoluteBoundingBox;
      const boundsB = b.absoluteBoundingBox;
      if (!boundsA || !boundsB) return 0;
      
      return direction === 'horizontal' 
        ? boundsA.x - boundsB.x 
        : boundsA.y - boundsB.y;
    });

    for (let i = 0; i < sortedNodes.length - 1; i++) {
      const currentBounds = sortedNodes[i].absoluteBoundingBox;
      const nextBounds = sortedNodes[i + 1].absoluteBoundingBox;
      
      if (currentBounds && nextBounds) {
        const spacing = direction === 'horizontal'
          ? nextBounds.x - (currentBounds.x + currentBounds.width)
          : nextBounds.y - (currentBounds.y + currentBounds.height);
        
        spacings.push(spacing);
      }
    }

    return spacings;
  }

  /**
   * 检查间距是否一致
   */
  private isConsistentSpacing(spacings: number[]): boolean {
    if (spacings.length < 2) return true;

    const tolerance = 2; // 2px 容差
    const referenceSpacing = spacings[0];

    return spacings.every(spacing => 
      Math.abs(spacing - referenceSpacing) <= tolerance
    );
  }

  /**
   * 检查层级结构
   */
  private checkHierarchy(node: any): LayoutIssue[] {
    const issues: LayoutIssue[] = [];
    const depth = this.calculateDepth(node);

    if (depth > 8) {
      issues.push({
        type: "poor_hierarchy",
        severity: "medium",
        description: `嵌套层级过深 (${depth} 层)，建议简化结构`,
        affectedNodes: [node.id]
      });
    }

    // 检查是否有不必要的嵌套
    const unnecessaryNesting = this.findUnnecessaryNesting(node);
    issues.push(...unnecessaryNesting);

    return issues;
  }

  /**
   * 查找不必要的嵌套
   */
  private findUnnecessaryNesting(node: any): LayoutIssue[] {
    const issues: LayoutIssue[] = [];

    if (node.children && Array.isArray(node.children)) {
      for (const child of node.children) {
        // 检查单子节点容器
        if (child.children && child.children.length === 1) {
          const grandchild = child.children[0];
          
          // 如果子节点和孙子节点类型相同且没有特殊样式，可能是不必要的嵌套
          if (child.type === grandchild.type && 
              this.hasSimilarProperties(child, grandchild)) {
            issues.push({
              type: "poor_hierarchy",
              severity: "low",
              description: `发现可能的不必要嵌套: "${child.name}"`,
              affectedNodes: [child.id]
            });
          }
        }

        // 递归检查子节点
        issues.push(...this.findUnnecessaryNesting(child));
      }
    }

    return issues;
  }

  /**
   * 检查两个节点是否有相似属性
   */
  private hasSimilarProperties(nodeA: any, nodeB: any): boolean {
    // 简单检查：如果两个节点都没有特殊的视觉属性，可能是不必要的嵌套
    const hasVisualPropsA = nodeA.fills?.length > 0 || nodeA.strokes?.length > 0 || nodeA.effects?.length > 0;
    const hasVisualPropsB = nodeB.fills?.length > 0 || nodeB.strokes?.length > 0 || nodeB.effects?.length > 0;

    return !hasVisualPropsA && !hasVisualPropsB;
  }
}

/**
 * 布局分析规则
 */
export const layoutAnalysisRule: Rule = {
  name: "layout-analysis",
  description: "分析布局结构并识别潜在问题",
  category: "layout",
  severity: "warning",
  enabled: true,
  validate: async (context: RuleContext): Promise<RuleResult> => {
    const traceId = context.traceId;
    logger.info(`[${traceId}] 开始布局分析`);

    try {
      const analyzer = new LayoutAnalyzer();
      const result = analyzer.analyzeLayout(context.node);

      const highSeverityIssues = result.issues.filter(issue => issue.severity === "high");
      const mediumSeverityIssues = result.issues.filter(issue => issue.severity === "medium");

      const suggestions = [
        ...result.recommendations,
        ...highSeverityIssues.map(issue => `高优先级: ${issue.description}`),
        ...mediumSeverityIssues.map(issue => `中优先级: ${issue.description}`)
      ];

      const valid = highSeverityIssues.length === 0 && mediumSeverityIssues.length <= 2;

      logger.info(`[${traceId}] 布局分析完成: 类型=${result.layoutType}, 复杂度=${result.complexity}, 问题数=${result.issues.length}`);

      return {
        valid,
        message: valid 
          ? `布局分析通过 (${result.layoutType} 布局, ${result.complexity} 复杂度)`
          : `发现 ${result.issues.length} 个布局问题`,
        severity: highSeverityIssues.length > 0 ? "error" : 
                 mediumSeverityIssues.length > 0 ? "warning" : "info",
        suggestions,
        fixes: result.issues.length > 0 ? [{
          type: 'modify',
          target: 'layout-structure',
          value: result.recommendations,
          description: `优化布局结构 (${result.issues.length} 个问题)`
        }] : undefined
      };
    } catch (error) {
      logger.error(`[${traceId}] 布局分析失败: ${error}`);
      return {
        valid: false,
        message: `布局分析失败: ${error}`,
        severity: "error",
        suggestions: ["检查节点结构是否正确"]
      };
    }
  }
};

/**
 * 自动布局优化规则
 */
export const autoLayoutOptimizationRule: Rule = {
  name: "auto-layout-optimization",
  description: "检查并优化自动布局的使用",
  category: "layout",
  severity: "info",
  enabled: true,
  validate: async (context: RuleContext): Promise<RuleResult> => {
    const traceId = context.traceId;
    logger.info(`[${traceId}] 开始自动布局优化分析`);

    try {
      const suggestions: string[] = [];
      const autoLayoutCandidates = findAutoLayoutCandidates(context.node);

      if (autoLayoutCandidates.length > 0) {
        suggestions.push(`发现 ${autoLayoutCandidates.length} 个可以使用自动布局的容器`);
        suggestions.push("考虑将规律排列的元素转换为自动布局");
      }

      const inefficientLayouts = findInefficientLayouts(context.node);
      if (inefficientLayouts.length > 0) {
        suggestions.push(`发现 ${inefficientLayouts.length} 个可以优化的自动布局`);
      }

      logger.info(`[${traceId}] 自动布局分析完成: 候选${autoLayoutCandidates.length}个, 可优化${inefficientLayouts.length}个`);

      return {
        valid: true,
        message: `自动布局优化分析完成，发现 ${autoLayoutCandidates.length + inefficientLayouts.length} 个优化机会`,
        severity: "info",
        suggestions
      };
    } catch (error) {
      logger.error(`[${traceId}] 自动布局优化分析失败: ${error}`);
      return {
        valid: false,
        message: `自动布局优化分析失败: ${error}`,
        severity: "error",
        suggestions: ["检查节点结构是否正确"]
      };
    }
  }
};

/**
 * 查找自动布局候选者
 */
function findAutoLayoutCandidates(node: any): any[] {
  const candidates: any[] = [];

  if (node.children && Array.isArray(node.children) && node.children.length >= 2) {
    // 检查是否为手动布局但元素排列规律
    if (!node.layoutMode || node.layoutMode === "NONE") {
      const isRegularlyArranged = checkRegularArrangement(node.children);
      if (isRegularlyArranged) {
        candidates.push(node);
      }
    }
  }

  // 递归检查子节点
  if (node.children && Array.isArray(node.children)) {
    for (const child of node.children) {
      candidates.push(...findAutoLayoutCandidates(child));
    }
  }

  return candidates;
}

/**
 * 检查元素是否规律排列
 */
function checkRegularArrangement(children: any[]): boolean {
  if (children.length < 2) return false;

  const bounds = children.map(child => child.absoluteBoundingBox).filter(Boolean);
  if (bounds.length !== children.length) return false;

  // 检查水平排列
  const sortedByX = [...bounds].sort((a, b) => a.x - b.x);
  const isHorizontal = checkConsistentSpacing(sortedByX, 'horizontal');

  // 检查垂直排列
  const sortedByY = [...bounds].sort((a, b) => a.y - b.y);
  const isVertical = checkConsistentSpacing(sortedByY, 'vertical');

  return isHorizontal || isVertical;
}

/**
 * 检查一致间距
 */
function checkConsistentSpacing(bounds: any[], direction: 'horizontal' | 'vertical'): boolean {
  if (bounds.length < 2) return false;

  const spacings: number[] = [];
  for (let i = 0; i < bounds.length - 1; i++) {
    const current = bounds[i];
    const next = bounds[i + 1];

    const spacing = direction === 'horizontal'
      ? next.x - (current.x + current.width)
      : next.y - (current.y + current.height);

    spacings.push(spacing);
  }

  // 检查间距是否一致（允许小幅差异）
  const tolerance = 5;
  const averageSpacing = spacings.reduce((sum, s) => sum + s, 0) / spacings.length;
  
  return spacings.every(spacing => Math.abs(spacing - averageSpacing) <= tolerance);
}

/**
 * 查找低效的布局
 */
function findInefficientLayouts(node: any): any[] {
  const inefficient: any[] = [];

  if (node.layoutMode && (node.layoutMode === "HORIZONTAL" || node.layoutMode === "VERTICAL")) {
    // 检查是否有不必要的嵌套自动布局
    if (node.children && node.children.length === 1) {
      const child = node.children[0];
      if (child.layoutMode === node.layoutMode) {
        inefficient.push(node);
      }
    }
  }

  // 递归检查子节点
  if (node.children && Array.isArray(node.children)) {
    for (const child of node.children) {
      inefficient.push(...findInefficientLayouts(child));
    }
  }

  return inefficient;
}